/**
 * CurrentlyEditingDisplay Component
 *
 * Shows the currently selected item in edit mode as a read-only display
 * above the search table. Provides immediate context for what the user is editing.
 *
 * Only shown in edit mode (when preselectedKey is present).
 */

import { CheckCircleIcon } from '@heroicons/react/24/outline';

import { StatusChip } from '@/components/legacy-ui/StatusChip';
import { cn } from '@/utils/classname';
import {
  assetStatusVariants,
  getVariantForStatus,
} from '@/utils/statusChip.util';

/**
 * Props for CurrentlyEditingDisplay component
 */
export interface CurrentlyEditingDisplayProps {
  /** Primary display name */
  name: string;
  /** Secondary metadata (IP for assets, group for vulnerabilities, ID for techniques) */
  metadata?: string;
  /** Status indicator (for assets/vulnerabilities) */
  status?: string;
  /** Category for techniques */
  category?: string;
  /** Whether this is a custom (non-Chariot) entity */
  isCustom?: boolean;
}

/**
 * CurrentlyEditingDisplay shows a read-only card with the currently
 * selected item's information.
 *
 * Design:
 * - Subtle card with purple border accent
 * - Checkmark icon to indicate "current selection"
 * - Primary name + secondary metadata (if present)
 * - Status badge (if present)
 * - Dashed border for custom items
 *
 * @example
 * ```tsx
 * <CurrentlyEditingDisplay
 *   name="mlb.praetorian.com"
 *   metadata="104.154.50.197"
 *   status="A"
 * />
 * ```
 */
export function CurrentlyEditingDisplay({
  name,
  metadata,
  status,
  category,
  isCustom = false,
}: CurrentlyEditingDisplayProps) {
  // Build secondary text - category and/or metadata
  const secondaryText = category
    ? metadata
      ? `${category} \u2022 ${metadata}`
      : category
    : metadata;

  return (
    <div
      className={cn(
        'border-chariot-brand-primary/30 bg-chariot-background-secondary',
        'flex items-center gap-3 rounded-lg border px-4 py-3',
        isCustom && 'border-dashed'
      )}
      role="status"
      aria-label={`Currently editing: ${name}`}
      data-testid="currently-editing-display"
    >
      {/* Check icon */}
      <CheckCircleIcon className="text-chariot-brand-primary size-5 shrink-0" />

      {/* Content */}
      <div className="min-w-0 flex-1">
        <p
          className="text-chariot-text-primary truncate text-sm font-medium"
          title={name}
        >
          {name}
        </p>
        {secondaryText && (
          <p
            className="text-chariot-text-secondary truncate text-xs"
            title={secondaryText}
          >
            {secondaryText}
          </p>
        )}
      </div>

      {/* Status badge (optional) */}
      {status && (
        <div data-testid="currently-editing-status" className="shrink-0">
          <StatusChip
            label={status}
            variant={getVariantForStatus(status, assetStatusVariants)}
          />
        </div>
      )}
    </div>
  );
}
